/**
 * BreakevenDashboardPage Component
 *
 * Dashboard page displaying breakeven analysis with what-if scenario modelling.
 * Shows breakeven point, safety margin, and interactive scenario tools.
 *
 * Part of US-P7-003: Breakeven Analysis
 */

import { useState } from 'react'
import { PageLayout } from '@/components/layout'
import { Card, CardContent } from '@/components/ui/card'
import { useBreakevenWithScenarios } from '@/hooks/useBreakeven'
import PeriodSelector, { PERIOD_TYPES, calculatePeriod } from '@/components/costs/PeriodSelector'
import {
  BreakevenChart,
  BreakevenChartSkeleton,
  BreakevenMetrics,
  BreakevenMetricsSkeleton,
  WhatIfPanel,
  WhatIfPanelSkeleton
} from '@/components/breakeven'

/**
 * BreakevenDashboardPage Component
 *
 * @param {Object} props
 * @param {Object} props.user - Current user object
 * @param {Function} props.onLogout - Logout handler
 */
export default function BreakevenDashboardPage({ user, onLogout }) {
  // Period state
  const [periodType, setPeriodType] = useState(PERIOD_TYPES.TRAILING_12)
  const [periodData, setPeriodData] = useState(() => calculatePeriod(PERIOD_TYPES.TRAILING_12))

  // Fetch breakeven data with scenario support
  const {
    data,
    chartData,
    metrics,
    isLoading,
    error,
    refetch,

    // Scenario data
    scenario,
    comparison,
    strains,
    adjustments,

    // Scenario setters
    setFixedCostsAdjustment,
    setPriceAdjustmentPercent,
    toggleStrainExclusion,

    // Scenario actions
    calculateScenario,
    resetScenario,
    isCalculating,
    hasScenario
  } = useBreakevenWithScenarios({
    periodStart: periodData.start,
    periodEnd: periodData.end
  })

  /**
   * Handle period change
   */
  const handlePeriodChange = (newPeriodType, newPeriodData) => {
    setPeriodType(newPeriodType)
    setPeriodData(newPeriodData)
    // Reset scenario when period changes
    resetScenario()
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Breakeven Analysis</h1>
            <p className="text-muted-foreground mt-1">
              {periodData.label}
            </p>
          </div>

          {/* Period Selector */}
          <PeriodSelector
            value={periodType}
            onChange={handlePeriodChange}
          />
        </div>

        {/* Error State */}
        {error && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <p className="text-red-800">
                Failed to load breakeven data: {error}
              </p>
              <button
                onClick={refetch}
                className="mt-2 text-sm text-red-600 underline hover:text-red-700"
              >
                Try again
              </button>
            </CardContent>
          </Card>
        )}

        {/* Metrics Cards */}
        {isLoading ? (
          <BreakevenMetricsSkeleton />
        ) : (
          <BreakevenMetrics data={data} isLoading={isLoading} />
        )}

        {/* Main Content - Chart and What-If Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Chart - 2 columns */}
          <div className="lg:col-span-2">
            {isLoading ? (
              <BreakevenChartSkeleton />
            ) : (
              <BreakevenChart chartData={chartData} isLoading={isLoading} />
            )}
          </div>

          {/* What-If Panel - 1 column */}
          <div>
            {isLoading ? (
              <WhatIfPanelSkeleton />
            ) : (
              <WhatIfPanel
                strains={strains}
                adjustments={adjustments}
                setFixedCostsAdjustment={setFixedCostsAdjustment}
                setPriceAdjustmentPercent={setPriceAdjustmentPercent}
                toggleStrainExclusion={toggleStrainExclusion}
                calculate={calculateScenario}
                reset={resetScenario}
                scenario={scenario}
                comparison={comparison}
                isCalculating={isCalculating}
                baselineFixedCosts={data?.fixed_costs || 0}
              />
            )}
          </div>
        </div>

        {/* Cost Breakdown Info */}
        {!isLoading && data && (
          <Card className="bg-neutral-50 border-neutral-200">
            <CardContent className="pt-4 pb-4 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <p className="text-neutral-500">Fixed Costs</p>
                  <p className="font-medium text-neutral-900">
                    {new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(data.fixed_costs || 0)}
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">Rent, insurance, and other overheads</p>
                </div>
                <div>
                  <p className="text-neutral-500">Variable Cost/kg</p>
                  <p className="font-medium text-neutral-900">
                    {new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(data.variable_cost_per_kg || 0)}
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">Cost to produce each kg</p>
                </div>
                <div>
                  <p className="text-neutral-500">Contribution/kg</p>
                  <p className="font-medium text-neutral-900">
                    {new Intl.NumberFormat('en-GB', {
                      style: 'currency',
                      currency: 'GBP',
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2
                    }).format(data.weighted_avg_contribution_per_kg || 0)}
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">Revenue minus variable cost per kg</p>
                </div>
                <div>
                  <p className="text-neutral-500">Contribution %</p>
                  <p className="font-medium text-neutral-900">
                    {(data.weighted_avg_contribution_percent || 0).toFixed(1)}%
                  </p>
                  <p className="text-xs text-neutral-400 mt-0.5">Share of revenue covering fixed costs</p>
                </div>
              </div>

              {/* Variable Cost Split */}
              {data.variable_costs_breakdown && data.variable_costs_breakdown.total > 0 && (() => {
                const b = data.variable_costs_breakdown
                const total = b.total
                const segments = [
                  { key: 'substrate', label: 'Substrate', value: b.substrate, colour: 'bg-emerald-500' },
                  { key: 'spawn', label: 'Spawn', value: b.spawn, colour: 'bg-teal-500' },
                  { key: 'shiitake', label: 'Shiitake logs', value: b.shiitake_logs, colour: 'bg-amber-500' },
                  { key: 'energy', label: 'Energy', value: b.energy, colour: 'bg-orange-500' },
                ].filter(s => s.value > 0)

                const fmt = (v) => new Intl.NumberFormat('en-GB', {
                  style: 'currency', currency: 'GBP',
                  minimumFractionDigits: 0, maximumFractionDigits: 0
                }).format(v)

                return (
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-neutral-500 uppercase tracking-wide">Variable Cost Breakdown</p>
                    <div className="flex h-3 rounded-full overflow-hidden bg-neutral-200">
                      {segments.map(s => (
                        <div
                          key={s.key}
                          className={`${s.colour} transition-all`}
                          style={{ width: `${(s.value / total) * 100}%` }}
                          title={`${s.label}: ${fmt(s.value)}`}
                        />
                      ))}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-neutral-600">
                      {segments.map(s => (
                        <span key={s.key} className="flex items-center gap-1.5">
                          <span className={`inline-block w-2.5 h-2.5 rounded-full ${s.colour}`} />
                          {s.label} {fmt(s.value)}
                        </span>
                      ))}
                    </div>
                  </div>
                )
              })()}
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {!isLoading && !error && data?.status !== 'ok' && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-12">
                <p className="text-neutral-500 mb-2">
                  Unable to calculate breakeven for this period
                </p>
                <p className="text-sm text-neutral-400">
                  {data?.status === 'zero_contribution'
                    ? 'No sales data or contribution margin data available. Add sales and ensure products have pricing set.'
                    : data?.status === 'negative_contribution'
                      ? 'Current pricing does not cover variable costs. Review your pricing strategy.'
                      : 'Fixed costs and sales data are required to calculate breakeven.'
                  }
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </PageLayout>
  )
}
